import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useTranslation } from '../../i18n';

// STD-015: server state for the Manufacturing domain. apiService is imported
// here (a *.queries.ts file) per STD-016. Errors are toasted by the global
// QueryCache handler (lib/queryClient); meta.errorMessage supplies the i18n
// fallback. Components render inline error UI from query.isError using static
// i18n strings — they never call showError for query failures.

export const manufacturingKeys = {
  all: ['manufacturing'] as const,
  access: () => [...manufacturingKeys.all, 'access'] as const,
  syncStatus: () => [...manufacturingKeys.all, 'sync-status'] as const,
  products: () => [...manufacturingKeys.all, 'products'] as const,
  workCenters: () => [...manufacturingKeys.all, 'work-centers'] as const,
  boms: () => [...manufacturingKeys.all, 'boms'] as const,
  productionOrders: () => [...manufacturingKeys.all, 'production-orders'] as const,
  onshapeDocuments: () => [...manufacturingKeys.all, 'onshape-documents'] as const,
  onshapeParts: () => [...manufacturingKeys.all, 'onshape-parts'] as const,
  drift: () => [...manufacturingKeys.all, 'drift'] as const,
  partDetail: (partId: string) => [...manufacturingKeys.all, 'part-detail', partId] as const,
  partsByCode: (codes: string[]) =>
    [...manufacturingKeys.all, 'parts-by-code', [...codes].sort()] as const,
};

export function useManufacturingAccessQuery() {
  // Entitlement probe — a failed/denied check simply means hasAccess:false, so
  // it opts out of the global error toast (silent, matching the other access
  // hooks and the original console.error-only behavior).
  return useQuery({
    queryKey: manufacturingKeys.access(),
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const res = await apiService.checkManufacturingAccess();
      return res.has_access;
    },
  });
}

export function useManufacturingSyncStatusQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.syncStatus(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingSyncStatus(),
  });
}

export function useManufacturingProductsQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.products(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingProducts(),
  });
}

export function useManufacturingWorkCentersQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.workCenters(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingWorkCenters(),
  });
}

export function useManufacturingBomsQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.boms(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingBoms(),
  });
}

export function useManufacturingProductionOrdersQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.productionOrders(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingProductionOrders(),
  });
}

export function useManufacturingOnshapeDocumentsQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.onshapeDocuments(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingOnshapeDocuments(),
  });
}

export function useManufacturingOnshapePartsQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.onshapeParts(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingOnshapeParts(),
  });
}

export function useManufacturingDriftQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.drift(),
    enabled,
    meta: { errorMessage: t('manufacturing.failedToLoad') },
    queryFn: () => apiService.getManufacturingDrift(),
  });
}

export function useManufacturingPartDetailQuery(partId: string | undefined, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: manufacturingKeys.partDetail(partId ?? ''),
    enabled: enabled && !!partId,
    meta: { errorMessage: t('manufacturing.failedToLoadPartDetail') },
    queryFn: () => apiService.getManufacturingPartDetail(partId as string),
  });
}

// Onshape part + drawing/PDF lookup keyed by Odoo default_code. Non-fatal: when
// the lookup fails the BomTree chips just stay grey, so this opts out of the
// global error toast (skipGlobalErrorToast) — matching the original page's
// console.warn-only behavior. Caller passes the deduped, <=500 code list.
export function useManufacturingOnshapePartsByCodeQuery(codes: string[], enabled: boolean) {
  return useQuery({
    queryKey: manufacturingKeys.partsByCode(codes),
    enabled: enabled && codes.length > 0,
    meta: { skipGlobalErrorToast: true },
    queryFn: () => apiService.getManufacturingOnshapePartsByCode(codes),
  });
}

// Fetch the backend-proxied PDF for an Onshape drawing element through the
// authenticated API client, returning a Blob the caller opens in a new tab.
// Exported as a plain function from this *.queries.ts file so components never
// import apiService directly (STD-016). It is a real (auth-bearing) fetch, not
// a URL builder — opening a bare `/api/...` URL hits the SPA, not the backend.
export function fetchManufacturingDrawingBlob(drawingElementId: string): Promise<Blob> {
  return apiService.downloadManufacturingDrawing(drawingElementId);
}
